import React, { useState, useEffect, useCallback } from 'react';
import Table from './components/Table';
import {
  Button,
  Offcanvas,
} from 'react-bootstrap';
import LoadingSpinner from '../../../../components/common/LoadingSpinner';
import {
  getPaymentTransactionsByLocalIdAndDateRange
} from '../../../../crud/payments_api/payment_transactions';
import { toast } from 'react-toastify';
import { Transaction } from '../types';
import TransactionSummary from './components/TransactionSummary/TransactionSummary';
import { FaFilter, FaFileExcel } from 'react-icons/fa';
import { X } from 'react-bootstrap-icons';
import DateFilter from './../Receipts/components/Filters/DateFilter';
import MultiSelectFilter from '../../../../components/common/MultiSelectFilter/MultiSelectFilter';
import CustomDatePicker from '../../../../components/common/CustomDatePicker/CustomDatePicker';
import CheckboxMultiSelect from 'components/common/CheckboxMultiSelect/CheckboxMultiSelect';
import { exportFilteredTransactions } from './utils/transactionsExcelExport';
import { LocalSession } from '../../../../types/LocalSession';
import { MPSwitch, MPPageTitle } from '../../../../components/ui';


interface TransactionsProps {
  lastSession?: LocalSession | null;
}

const Transactions: React.FC<TransactionsProps> = ({ lastSession }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [paymentType, setPaymentType] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [amountRange, setAmountRange] = useState<[number, number]>([0, 10000]);
  const [selectedStatus, setSelectedStatus] = useState<{value: string, label: string}[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Nuevo estado para activar/desactivar rango por monto
  const [enableAmountRange, setEnableAmountRange] = useState(false);

  // State for CheckboxMultiSelect components
  const [selectedPaymentTypes, setSelectedPaymentTypes] = useState<{value: any, label: string}[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<{value: any, label: string}[]>([]);

  // Change default to false so it doesn't override our date range
  const [soloSesionActual, setSoloSesionActual] = useState(false);
  
  // Default dates: start date = today - 2 days, end date = tomorrow
  const today = new Date();
  const twoDaysAgo = new Date(today);
  twoDaysAgo.setDate(today.getDate() - 2);
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  
  const [startDate, setStartDate] = useState(twoDaysAgo.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(tomorrow.toISOString().split('T')[0]);
  // (Receipts parity) No sincronizamos fechas al activar soloSesionActual; el filtro por sesión se hace client-side.

  const applyFilters = useCallback((data: Transaction[]) => {
    let filteredData = data;

    // Filter by status
    if (selectedStatus.length > 0) {
      filteredData = filteredData.filter((tx) => {
        return selectedStatus.some(status => {
          if (status.value === 'approved') return tx.approved;
          if (status.value === 'cancelled') return tx.canceled;
          if (status.value === 'pending') return !tx.approved && !tx.canceled;
          return false;
        });
      });
    }
    
    if (paymentType) {
      const paymentTypes = paymentType.toLowerCase().split(',');
      filteredData = filteredData.filter((tx) =>
        paymentTypes.some(pt => tx.payment_type_name.toLowerCase().includes(pt))
      );
    }
    if (userName) {
      const userNames = userName.toLowerCase().split(',');
      filteredData = filteredData.filter((tx) =>
        userNames.some(un => tx.user_name.toLowerCase().includes(un))
      );
    }

    // Sólo filtrar por monto si está habilitado
    if (enableAmountRange) {
      filteredData = filteredData.filter(
        (tx) =>
          tx.transaction_amount >= amountRange[0] &&
          tx.transaction_amount <= amountRange[1]
      );
    }

    if (startDate && endDate) {
      filteredData = filteredData.filter((tx) => {
        const txDate = new Date(tx.local_timestamp || tx.timestamp)
          .toISOString()
          .split('T')[0];
        return txDate >= startDate && txDate <= endDate;
      });
    }
    // Filtro de solo sesión actual (igual que en Receipts): comparar session_id/local_session_id sin alterar fechas
    if (soloSesionActual && lastSession?.local_session_id) {
      const targetSessionId = lastSession.local_session_id;
      filteredData = filteredData.filter(tx => {
        const txSessionId = (tx as any).session_id ?? (tx as any).local_session_id;
        return txSessionId === targetSessionId;
      });
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filteredData = filteredData.filter(tx => {
        return (
          tx.user_name?.toLowerCase().includes(query) ||
          tx.payment_type_name?.toLowerCase().includes(query) ||
          tx.transaction_amount?.toString().includes(query)
        );
      });
    }

    setFilteredTransactions(filteredData);
  }, [selectedStatus, paymentType, userName, enableAmountRange, amountRange, startDate, endDate, soloSesionActual, lastSession, searchQuery]);

  useEffect(() => {
    const userContext = localStorage.getItem('user');
    const localId = userContext ? JSON.parse(userContext).local_id : 0;

    setLoading(true);
    getPaymentTransactionsByLocalIdAndDateRange(localId, startDate, endDate)
      .then((response) => {
        if (response && response.data) {
          setTransactions(response.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching transactions: ", error);
        toast.error("Error al cargar las transacciones");
      })
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      applyFilters(transactions);
      setLoading(false);
    }, 300);
  }, [transactions, applyFilters]);

  // Helper de depuración manual (sin logs automáticos). Ejecutar en consola: window.__debugSessionTransactions()
  useEffect(() => {
    (window as any).__debugSessionTransactions = () => {
      console.log('=== DEBUG TRANSACCIONES Y SESIÓN ===');
      console.log('Sesión actual:', lastSession);
      console.log('Filtro activo:', soloSesionActual);
      
      const counts: Record<string, number> = {};
      transactions.forEach(tx => {
        const sid = String((tx as any).session_id ?? (tx as any).local_session_id ?? 'none');
        counts[sid] = (counts[sid] || 0) + 1;
      });
      
      console.log('Transacciones por session_id:', counts);
      console.log('Total transacciones:', transactions.length);
      console.log('Transacciones filtradas:', filteredTransactions.length);
      
      return {
        lastSession,
        soloSesionActual,
        transactionsBySessions: counts,
        totalTransactions: transactions.length,
        filteredTransactions: filteredTransactions.length
      };
    };
    return () => { delete (window as any).__debugSessionTransactions; };
  }, [transactions, filteredTransactions, lastSession, soloSesionActual]);

  // Reset selected values when transactions change to ensure consistency
  useEffect(() => {
    // Reset selected payment types if they don't exist in current transactions
    if (selectedPaymentTypes.length > 0) {
      const availablePaymentTypes = [...new Set(transactions.map((t) => t.payment_type_name))];
      const validSelected = selectedPaymentTypes.filter(selected => 
        availablePaymentTypes.includes(selected.value)
      );
      if (validSelected.length !== selectedPaymentTypes.length) {
        setSelectedPaymentTypes(validSelected);
        setPaymentType(validSelected.map(s => s.value).join(','));
      }
    }

    // Reset selected users if they don't exist in current transactions  
    if (selectedUsers.length > 0) {
      const availableUsers = [...new Set(transactions.map((t) => t.user_name))];
      const validSelected = selectedUsers.filter(selected => 
        availableUsers.includes(selected.value)
      );
      if (validSelected.length !== selectedUsers.length) {
        setSelectedUsers(validSelected);
        setUserName(validSelected.map(s => s.value).join(','));
      }
    }
  }, [transactions, selectedPaymentTypes, selectedUsers]);

  const handleStartDateChange= (newDate: string) => {
    // No desactivamos soloSesionActual (paridad con Receipts)
    setStartDate(newDate);
  };
  const handleEndDateChange = (newDate: string) => {
    // No desactivamos soloSesionActual (paridad con Receipts)
    setEndDate(newDate);
  };

  // Handle Excel export
  const handleExportToExcel = () => {
    if (!filteredTransactions || filteredTransactions.length === 0) {
      alert('No hay transacciones para exportar');
      return;
    }

    try {
      exportFilteredTransactions(filteredTransactions, startDate, endDate);
      toast.success('Excel exportado exitosamente');
    } catch (error) {
      console.error('Error exporting transactions to Excel:', error);
      toast.error('Error al exportar a Excel. Por favor, intenta nuevamente.');
    }
  };

  // Count active filters for mobile badge
  const activeFilterCount = [
    selectedPaymentTypes.length > 0,
    selectedUsers.length > 0,
    selectedStatus.length > 0,
    enableAmountRange,
    soloSesionActual,
  ].filter(Boolean).length;

  return (
    <div className="flex flex-col gap-4 p-3 pb-6 lg:gap-5 lg:p-4 lg:pb-8">
      {/* Page Header */}
      <MPPageTitle><span className="text-xl lg:text-2xl">Transacciones</span></MPPageTitle>

      {/* Filter Toolbar */}
      <div className="mp-toolbar">
        {/* Row 1: Search + Dates + Actions */}
        <div className="flex flex-wrap items-center gap-2 lg:gap-3">
          {/* Search */}
          <div className="flex-1 min-w-[180px] lg:min-w-[220px] max-w-[380px] relative">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 opacity-30" style={{ color: 'var(--text-color)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
              </svg>
              <input
                type="text"
                className="w-full rounded-lg border py-2 pl-9 pr-8 text-sm outline-none transition-all focus:ring-2"
                style={{
                  backgroundColor: 'var(--card-color)',
                  borderColor: 'var(--border-color)',
                  color: 'var(--text-color)',
                }}
                placeholder="Buscar transacciones..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 opacity-30 hover:opacity-70 transition-opacity"
                  onClick={() => setSearchQuery('')}
                  style={{ color: 'var(--text-color)' }}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Desktop: Date pickers + Session toggle */}
          <div className="hidden lg:flex items-center gap-2">
            <div className="min-w-[145px]">
              <CustomDatePicker
                value={startDate}
                onChange={handleStartDateChange}
                disabled={soloSesionActual}
                placeholder="Fecha inicio"
              />
            </div>
            <div className="min-w-[145px]">
              <CustomDatePicker
                value={endDate}
                onChange={handleEndDateChange}
                disabled={soloSesionActual}
                placeholder="Fecha fin"
              />
            </div>
            <MPSwitch
              checked={soloSesionActual}
              onChange={setSoloSesionActual}
              label="Sesión actual"
              size="sm"
              id="tx-session-toggle"
            />
          </div>

          {/* Mobile: Filter + Export buttons */}
          <button
            onClick={() => setShowFilters(true)}
            className="lg:hidden mp-toolbar-btn relative"
            aria-label="Abrir filtros"
          >
            <FaFilter className="text-xs" style={{ color: '#FC7556' }} />
            <span>Filtros</span>
            {activeFilterCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center rounded-full text-[10px] font-bold text-white" style={{ background: '#FC7556', minWidth: '18px', height: '18px' }}>
                {activeFilterCount}
              </span>
            )}
          </button>

          <button
            className="lg:hidden mp-btn-export !px-2.5 !gap-0"
            onClick={handleExportToExcel}
            disabled={loading || filteredTransactions.length === 0}
            title="Exportar a Excel"
          >
            <FaFileExcel className="text-sm" />
          </button>

          {/* Desktop Export */}
          <div className="hidden lg:block ml-auto">
            <button
              className="mp-btn-export"
              onClick={handleExportToExcel}
              disabled={loading || filteredTransactions.length === 0}
              title="Exportar transacciones a Excel"
            >
              <FaFileExcel />
              <span>Exportar</span>
            </button>
          </div>
        </div>

        {/* Row 2: Dropdown filters — desktop only */}
        <div className="hidden lg:flex items-center gap-2 mt-3 pt-3" style={{ borderTop: '1px solid var(--border-color)' }}>
          <span className="text-xs font-semibold uppercase tracking-wider opacity-35 mr-1" style={{ color: 'var(--text-color)' }}>Filtrar:</span>
          <div className="min-w-[140px]">
            <CheckboxMultiSelect
              options={[...new Set(transactions.map((t) => t.payment_type_name))]
                .map((type) => ({ value: type, label: type }))}
              value={selectedPaymentTypes}
              onChange={(selected) => {
                setSelectedPaymentTypes(selected);
                setPaymentType(selected.map((s: any) => s.value).join(','));
              }}
              placeholder="Tipo de pago"
            />
          </div>
          <div className="min-w-[140px]">
            <CheckboxMultiSelect
              options={[...new Set(transactions.map((t) => t.user_name))]
                .map((user) => ({ value: user, label: user }))}
              value={selectedUsers}
              onChange={(selected) => {
                setSelectedUsers(selected);
                setUserName(selected.map((s: any) => s.value).join(','));
              }}
              placeholder="Usuario"
            />
          </div>
          <div className="min-w-[140px]">
            <CheckboxMultiSelect
              options={[
                { value: 'approved', label: 'Aprobadas' },
                { value: 'cancelled', label: 'Canceladas' },
                { value: 'pending', label: 'Pendientes' }
              ]}
              value={selectedStatus}
              onChange={setSelectedStatus}
              placeholder="Estado"
            />
          </div>
        </div>
      </div>

      {/* Mobile Offcanvas Filters */}
      <Offcanvas
        show={showFilters}
        onHide={() => setShowFilters(false)}
        placement="end"
        className="mp-offcanvas transactions-offcanvas"
        backdrop={true}
        scroll={false}
      >
        <Offcanvas.Header className="offcanvas-header">
          <div className="header-content">
            <Offcanvas.Title className="offcanvas-title">Opciones de Filtrado</Offcanvas.Title>
            <div className="section-indicator">Transacciones</div>
          </div>
          <Button
            variant="none"
            className="close-button"
            onClick={() => setShowFilters(false)}
            aria-label="Cerrar filtros"
          >
            <X size={24} />
          </Button>
        </Offcanvas.Header>

        <Offcanvas.Body className="offcanvas-body">
          <div className="filters-container">
            <div className="filter-section">
              <h6 className="filter-section-title">Filtros Generales</h6>

              <div className="filter-group">
                <label className="filter-label">Rango de fechas</label>
                <div className="receipt-date-filter-container">
                  <DateFilter
                    startDate={startDate}
                    setStartDate={handleStartDateChange}
                    endDate={endDate}
                    setEndDate={handleEndDateChange}
                  />
                </div>
              </div>

              <div className="filter-group">
                <label className="filter-label">Tipo de pago</label>
                <div className="multiselect-container">
                  <MultiSelectFilter
                    options={[...new Set(transactions.map((t) => t.payment_type_name))]
                      .map((type) => ({ value: type, label: type }))}
                    onChange={(selected) => setPaymentType(selected.map((s: any) => s.value).join(','))}
                    placeholder="Filtrar por tipo de pago"
                  />
                </div>
              </div>

              <div className="filter-group">
                <label className="filter-label">Usuario</label>
                <div className="multiselect-container">
                  <MultiSelectFilter
                    options={[...new Set(transactions.map((t) => t.user_name))]
                      .map((user) => ({ value: user, label: user }))}
                    onChange={(selected) => setUserName(selected.map((s: any) => s.value).join(','))}
                    placeholder="Filtrar por usuario"
                  />
                </div>
              </div>

              <div className="filter-group">
                <label className="filter-label">Estado</label>
                <div className="multiselect-container">
                  <CheckboxMultiSelect
                    options={[
                      { label: 'Aprobadas', value: 'approved' },
                      { label: 'Canceladas', value: 'cancelled' },
                      { label: 'Pendientes', value: 'pending' }
                    ]}
                    value={selectedStatus}
                    onChange={setSelectedStatus}
                    placeholder="Seleccionar estado"
                  />
                </div>
              </div>

              <div className="filter-group">
                <MPSwitch
                  checked={soloSesionActual}
                  onChange={setSoloSesionActual}
                  label="Solo sesión actual"
                  id="tx-session-toggle-mobile"
                />
              </div>
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Summary Stats */}
      <TransactionSummary transactions={filteredTransactions} />

      {/* Table Content */}
      {loading ? (
        <LoadingSpinner text="Cargando transacciones" transparent />
      ) : filteredTransactions.length > 0 ? (
        <Table data={filteredTransactions} />
      ) : (
        <div className="mp-table-card flex items-center justify-center py-12">
          <p className="text-sm opacity-40" style={{ color: 'var(--text-color)' }}>
            ¡No se han registrado transacciones hoy! Para visualizar anteriores utilice los filtros
          </p>
        </div>
      )}
    </div>
  );
};

export default Transactions;
